import { useEffect, useState } from 'react'
import { get, onValue, ref } from 'firebase/database'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'

function toMillis(value) {
  if (value == null) return null
  if (typeof value === 'number') return value
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return value.seconds * 1000
  }
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

export function formatDeviceAssociationDate(value) {
  const ms = toMillis(value)
  if (!ms) return null
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(ms))
}

/**
 * Lista dispositivos em users/{uid}/dispositivosAssociados + nome em devices/{id}/config.
 */
export function useUserDevices() {
  const { user } = useAuth()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(Boolean(user))
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!user?.uid) {
      setDevices([])
      setLoading(false)
      setError(null)
      return undefined
    }

    setLoading(true)
    const assocRef = ref(db, `users/${user.uid}/dispositivosAssociados`)

    const unsub = onValue(
      assocRef,
      async (snap) => {
        try {
          const data = snap.val() || {}
          const ids = Object.keys(data)

          if (ids.length === 0) {
            setDevices([])
            setError(null)
            setLoading(false)
            return
          }

          const list = await Promise.all(
            ids.map(async (deviceId) => {
              const assoc = data[deviceId] || {}
              let name = null
              let isOwner = false

              try {
                const configSnap = await get(ref(db, `devices/${deviceId}/config`))
                if (configSnap.exists()) {
                  const config = configSnap.val() || {}
                  name = config.name || null
                  isOwner = String(config.ownerUid || '') === user.uid
                }
              } catch {
                // regras podem bloquear leitura pontual; ainda listamos o ID
              }

              return {
                id: deviceId,
                name: name || `Dispositivo ${deviceId.slice(-4)}`,
                associationDate: assoc.dataAssociacao || null,
                notificationsEnabled: assoc.notificacoesAtivas !== false,
                isOwner,
              }
            })
          )

          list.sort((a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR'))
          setDevices(list)
          setError(null)
        } catch (err) {
          console.error('Erro ao carregar dispositivos:', err)
          setError(err)
          setDevices([])
        } finally {
          setLoading(false)
        }
      },
      (err) => {
        setError(err)
        setDevices([])
        setLoading(false)
      }
    )

    return () => unsub()
  }, [user?.uid])

  return { devices, loading, error }
}
